import React, {Component, ReactElement} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import NatCondominiumRepo from '../condominium/repository/NatCondominiumRepo'
import {Nullable} from '../../../app/TypeHelpers'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {ADMINISTORATOR_MODE, DISASTER_TOPIC_CATEGORY, MESSAGE_TYPE} from '../../../CodeMaster'
import {NatAdminDisasterTopicForm} from './admin/NatAdminDisasterTopicForm'
import NatDisasterRepo from './repository/NatDisasterRepo'
import {Messages} from '../../../Messages'
import {NatPostAdminDisasterRegisterParams} from './repository/NatPostAdminDisasterRegister'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumName: Nullable<string>
    isLoadCompleted: boolean
}

class NatAdminDisasterTopicRegister extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.SELECTED_MODE)

        this.state = {
            condominiumName: null,
            isLoadCompleted: false
        }

        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleRegisterTopicButtonClink = this.handleRegisterTopicButtonClink.bind(this)
        this.handleBackLinkClick = this.handleBackLinkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                this.setState({condominiumName: await this.getCondominiumName()})
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getCondominiumName(): Promise<Nullable<string>> {
        const {condominiumCd, condominiumRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.condominiumName ?? null
    }

    async handleRegisterTopicButtonClink(registerParam: NatPostAdminDisasterRegisterParams): Promise<void> {
        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0012.errorLevel,
                message: Messages.IOPCC0012.toString('トピック'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: () => this.props.eventEmitter.closeDialog()
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: () => this.handleRegisterOkClick(registerParam)
                    }
                ]
            }
        )
    }

    async handleRegisterOkClick(registerParam: NatPostAdminDisasterRegisterParams): Promise<void> {
        const {condominiumCd, disasterRepo, eventEmitter} = this.props

        eventEmitter.closeDialog()
        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                disasterRepo.registerDisasterTopic.bind(disasterRepo, condominiumCd, registerParam),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0001.toString('トピック')}
            )
            if (response && response.details && response.requestError === null) {
                this.props.navigate(`/admin/condominiums/${condominiumCd}/disasters/topics/${response.details.topicId}`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0004.toString('トピック', registerParam.title)
                            }
                        }
                    }
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleBackLinkClick(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/disasters/topics`)
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        return (
            <NatAdminDisasterTopicForm
                mode={'register'}
                condominiumName={this.state.condominiumName}
                category={DISASTER_TOPIC_CATEGORY.REQUEST_ITEMS}
                eventEmitter={this.props.eventEmitter}
                onRegisterTopic={this.handleRegisterTopicButtonClink}
                backButtonText={'災害時情報共有一覧へ戻る'}
                onBack={this.handleBackLinkClick}
            />
        )
    }
}

export default createAdminPage(NatAdminDisasterTopicRegister)
